import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
export function TermsOfServicePage() {
  return <>
      <Helmet data-id="element-10283">
        <title data-id="element-10284">Terms of Service | ToxicDetector</title>
        <meta name="description" content="Read the terms and conditions for using ToxicDetector's AI-powered message analysis tools." data-id="element-10285" />
        <link rel="canonical" href="https://toxicdetector.com/terms" data-id="element-10286" />
        <meta property="og:title" content="Terms of Service | ToxicDetector" data-id="element-10287" />
        <meta property="og:description" content="Read the terms and conditions for using ToxicDetector's AI-powered message analysis tools." data-id="element-10288" />
        <meta property="og:url" content="https://toxicdetector.com/terms" data-id="element-10289" />
        <meta property="og:type" content="website" data-id="element-10290" />
        <meta name="robots" content="index, follow" data-id="element-10291" />
      </Helmet>
      <div className="flex flex-col w-full min-h-screen bg-[#f8f6f2]" data-id="element-10292">
        <header className="bg-white border-b border-gray-200" data-id="element-10293">
          <div className="max-w-4xl mx-auto px-5 py-4" data-id="element-10294">
            <Link to="/" className="inline-flex items-center text-gray-600 hover:text-purple-600 text-sm mb-2" data-id="element-10295">
              <ArrowLeft size={16} className="mr-1" data-id="element-10296" />
              Back to Home
            </Link>
            <h1 className="text-2xl font-bold" data-id="element-10297">Terms of Service</h1>
            <p className="text-gray-600 text-sm mt-1" data-id="element-10298">
              Last updated:{' '}
              {new Date().toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            })}
            </p>
          </div>
        </header>

        <div className="max-w-4xl mx-auto w-full px-5 py-8" data-id="element-10299">
          <div className="bg-white rounded-xl p-8 shadow-sm" data-id="element-10300">
            <section className="mb-8" data-id="element-10301">
              <h2 className="text-xl font-bold mb-4" data-id="element-10302">Agreement to Terms</h2>
              <p className="text-gray-700 mb-4" data-id="element-10303">
                Welcome to ToxicDetector. These Terms of Service ("Terms")
                govern your access to and use of our website, application, and
                services (collectively, the "Services"). By accessing or using
                our Services, you agree to be bound by these Terms and our
                Privacy Policy.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-10304">
                If you do not agree to these Terms, you may not access or use
                our Services. We reserve the right to modify these Terms at any
                time, and your continued use of the Services constitutes
                acceptance of any changes.
              </p>
              <p className="text-gray-700" data-id="element-10305">
                <strong data-id="element-10306">IMPORTANT:</strong> These Terms contain an arbitration
                clause and class action waiver that affect your legal rights.
                Please read Section 14 carefully.
              </p>
            </section>

            <section className="mb-8" data-id="element-10307">
              <h2 className="text-xl font-bold mb-4" data-id="element-10308">
                1. Description of Services
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-10309">
                ToxicDetector is an AI-powered platform that analyzes text
                conversations for patterns of toxic behavior, including but not
                limited to gaslighting, manipulation, emotional abuse, and other
                harmful communication patterns. Our Services include:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10310">
                <li data-id="element-10311">Screenshot upload and secure storage</li>
                <li data-id="element-10312">
                  AI-powered toxicity analysis using advanced natural language
                  processing
                </li>
                <li data-id="element-10313">Detailed reports on detected patterns and risk levels</li>
                <li data-id="element-10314">Scan history and result management</li>
                <li data-id="element-10315">Result sharing capabilities</li>
                <li data-id="element-10316">User account management and privacy controls</li>
              </ul>
              <p className="text-gray-700" data-id="element-10317">
                ToxicDetector is provided for informational and educational
                purposes only. Our analysis is not a substitute for professional
                advice from qualified mental health professionals, therapists,
                counselors, or legal advisors.
              </p>
            </section>

            <section className="mb-8" data-id="element-10318">
              <h2 className="text-xl font-bold mb-4" data-id="element-10319">
                2. Eligibility and Account Registration
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10320">
                2.1 Age Requirements
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10321">
                You must be at least 18 years old to use ToxicDetector. By using
                our Services, you represent and warrant that you are at least 18
                years of age and have the legal capacity to enter into these
                Terms.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10322">
                2.2 Account Creation
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10323">
                To access certain features, you must create an account. You
                agree to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10324">
                <li data-id="element-10325">
                  Provide accurate, current, and complete information during
                  registration
                </li>
                <li data-id="element-10326">Maintain and promptly update your account information</li>
                <li data-id="element-10327">
                  Maintain the security of your password and account credentials
                </li>
                <li data-id="element-10328">
                  Accept responsibility for all activities that occur under your
                  account
                </li>
                <li data-id="element-10329">
                  Notify us immediately of any unauthorized access or security
                  breach
                </li>
              </ul>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10330">
                2.3 Account Security
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10331">
                You are solely responsible for maintaining the confidentiality
                of your account credentials. We strongly recommend enabling
                two-factor authentication (2FA) for enhanced security. You agree
                not to share your account with others or allow others to access
                your account.
              </p>
            </section>

            <section className="mb-8" data-id="element-10332">
              <h2 className="text-xl font-bold mb-4" data-id="element-10333">
                3. Acceptable Use Policy
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10334">3.1 Permitted Uses</h3>
              <p className="text-gray-700 mb-4" data-id="element-10335">
                You may use ToxicDetector only for lawful purposes and in
                accordance with these Terms. You agree to use the Services only
                to analyze conversations to which you are a party or for which
                you have obtained proper consent.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10336">
                3.2 Prohibited Uses
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10337">You agree NOT to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10338">
                <li data-id="element-10339">
                  Upload conversations or content that you do not have the right
                  to analyze
                </li>
                <li data-id="element-10340">
                  Violate any applicable laws, regulations, or third-party
                  rights
                </li>
                <li data-id="element-10341">
                  Upload content containing child exploitation material, illegal
                  content, or content that violates others' privacy
                </li>
                <li data-id="element-10342">
                  Use the Services to harass, abuse, stalk, threaten, or
                  intimidate others
                </li>
                <li data-id="element-10343">
                  Attempt to gain unauthorized access to our systems or other
                  users' accounts
                </li>
                <li data-id="element-10344">
                  Reverse engineer, decompile, or attempt to extract source code
                  from our Services
                </li>
                <li data-id="element-10345">
                  Use automated systems (bots, scrapers) to access the Services
                  without permission
                </li>
                <li data-id="element-10346">Interfere with or disrupt the Services or servers</li>
                <li data-id="element-10347">
                  Impersonate any person or entity or misrepresent your
                  affiliation
                </li>
                <li data-id="element-10348">
                  Use the Services for any commercial purpose without our
                  written consent
                </li>
                <li data-id="element-10349">Remove, obscure, or alter any legal notices or branding</li>
              </ul>
            </section>

            <section className="mb-8" data-id="element-10350">
              <h2 className="text-xl font-bold mb-4" data-id="element-10351">
                4. User Content and Consent
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10352">4.1 Your Content</h3>
              <p className="text-gray-700 mb-4" data-id="element-10353">
                You retain all ownership rights to the content you upload ("User
                Content"). By uploading content to ToxicDetector, you represent
                and warrant that:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10354">
                <li data-id="element-10355">
                  You own or have the necessary rights to upload and analyze the
                  content
                </li>
                <li data-id="element-10356">
                  The content does not violate any laws or third-party rights
                </li>
                <li data-id="element-10357">
                  You have obtained all necessary consents from other parties in
                  the conversation
                </li>
                <li data-id="element-10358">
                  The content does not contain illegal material or violate our
                  Acceptable Use Policy
                </li>
              </ul>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10359">4.2 License Grant</h3>
              <p className="text-gray-700 mb-4" data-id="element-10360">
                By uploading User Content, you grant ToxicDetector a limited,
                non-exclusive, worldwide, royalty-free license to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10361">
                <li data-id="element-10362">Store and process your content to provide the Services</li>
                <li data-id="element-10363">Perform AI analysis on your content</li>
                <li data-id="element-10364">Display analysis results to you</li>
                <li data-id="element-10365">
                  Create aggregated, anonymized data for service improvement (no
                  personal identifiers)
                </li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-10366">
                This license terminates when you delete your content or account,
                except for content you have chosen to share publicly.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10367">
                4.3 Content Removal
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10368">
                We reserve the right to remove any User Content that violates
                these Terms, our policies, or applicable law. We may also
                suspend or terminate accounts that repeatedly violate our
                policies.
              </p>
            </section>

            <section className="mb-8" data-id="element-10369">
              <h2 className="text-xl font-bold mb-4" data-id="element-10370">
                5. Intellectual Property Rights
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10371">
                5.1 Our Intellectual Property
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10372">
                ToxicDetector and all associated content, features,
                functionality, software, algorithms, designs, logos, and
                trademarks are owned by ToxicDetector and are protected by
                copyright, trademark, patent, and other intellectual property
                laws.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-10373">
                <strong data-id="element-10374">
                  © {new Date().getFullYear()} ToxicDetector. All rights
                  reserved.
                </strong>
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10375">
                5.2 Limited License
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10376">
                Subject to these Terms, we grant you a limited, non-exclusive,
                non-transferable, revocable license to access and use the
                Services for your personal, non-commercial use. This license
                does not include any right to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10377">
                <li data-id="element-10378">Resell or make commercial use of the Services</li>
                <li data-id="element-10379">Modify, copy, or create derivative works</li>
                <li data-id="element-10380">
                  Download or extract any portion of the Services (except as
                  expressly permitted)
                </li>
                <li data-id="element-10381">
                  Use any data mining, robots, or similar data gathering tools
                </li>
              </ul>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10382">5.3 Feedback</h3>
              <p className="text-gray-700 mb-4" data-id="element-10383">
                If you provide feedback, suggestions, or ideas about our
                Services, you grant us the right to use such feedback without
                compensation or attribution.
              </p>
            </section>

            <section className="mb-8" data-id="element-10384">
              <h2 className="text-xl font-bold mb-4" data-id="element-10385">
                6. AI Analysis and Accuracy
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10386">
                6.1 Nature of AI Analysis
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10387">
                Our toxicity analysis is powered by artificial intelligence and
                machine learning algorithms. While we strive for accuracy, AI
                analysis has inherent limitations and may not always be 100%
                accurate or complete.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10388">6.2 No Guarantees</h3>
              <p className="text-gray-700 mb-4" data-id="element-10389">
                We do not guarantee that our analysis will:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10390">
                <li data-id="element-10391">Detect all instances of toxic behavior</li>
                <li data-id="element-10392">Be free from false positives or false negatives</li>
                <li data-id="element-10393">
                  Account for context, sarcasm, cultural differences, or nuanced
                  communication
                </li>
                <li data-id="element-10394">
                  Provide legally admissible evidence or professional
                  psychological assessment
                </li>
              </ul>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10395">
                6.3 Not Professional Advice
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10396">
                <strong data-id="element-10397">IMPORTANT:</strong> ToxicDetector's analysis is for
                informational purposes only and does not constitute professional
                advice. Our Services are not a substitute for:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10398">
                <li data-id="element-10399">Mental health counseling or therapy</li>
                <li data-id="element-10400">Legal advice or representation</li>
                <li data-id="element-10401">Medical diagnosis or treatment</li>
                <li data-id="element-10402">Professional relationship counseling</li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-10403">
                If you are experiencing abuse, harassment, or mental health
                concerns, please seek help from qualified professionals, law
                enforcement, or support organizations.
              </p>
            </section>

            <section className="mb-8" data-id="element-10404">
              <h2 className="text-xl font-bold mb-4" data-id="element-10405">
                7. Privacy and Data Protection
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-10406">
                Your privacy is important to us. Our collection, use, and
                protection of your personal information is governed by our
                Privacy Policy, which is incorporated into these Terms by
                reference. By using our Services, you consent to our Privacy
                Policy.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-10407">Key privacy points:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10408">
                <li data-id="element-10409">We encrypt all data in transit and at rest</li>
                <li data-id="element-10410">You control your data retention settings</li>
                <li data-id="element-10411">You can delete your data at any time</li>
                <li data-id="element-10412">We do not sell your personal information</li>
                <li data-id="element-10413">
                  We comply with GDPR, CCPA, and other privacy regulations
                </li>
              </ul>
            </section>

            <section className="mb-8" data-id="element-10414">
              <h2 className="text-xl font-bold mb-4" data-id="element-10415">
                8. Disclaimers and Limitations of Liability
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10416">
                8.1 Service Provided "As Is"
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10417">
                THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS
                WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
                INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY,
                FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR ACCURACY.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10418">8.2 No Warranty</h3>
              <p className="text-gray-700 mb-4" data-id="element-10419">We do not warrant that:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10420">
                <li data-id="element-10421">
                  The Services will be uninterrupted, timely, secure, or
                  error-free
                </li>
                <li data-id="element-10422">
                  The results obtained from using the Services will be accurate
                  or reliable
                </li>
                <li data-id="element-10423">Any errors in the Services will be corrected</li>
                <li data-id="element-10424">The Services will meet your specific requirements</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10425">
                8.3 Limitation of Liability
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10426">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, TOXICDETECTOR SHALL NOT
                BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
                OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER
                INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE,
                GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10427">
                <li data-id="element-10428">Your use or inability to use the Services</li>
                <li data-id="element-10429">
                  Any unauthorized access to or use of our servers and/or any
                  personal information stored therein
                </li>
                <li data-id="element-10430">
                  Any interruption or cessation of transmission to or from the
                  Services
                </li>
                <li data-id="element-10431">
                  Any bugs, viruses, or similar issues transmitted through the
                  Services
                </li>
                <li data-id="element-10432">
                  Any errors or omissions in any content or for any loss or
                  damage incurred as a result of the use of any content posted,
                  emailed, transmitted, or otherwise made available through the
                  Services
                </li>
                <li data-id="element-10433">
                  The accuracy or reliability of any AI analysis or results
                </li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-10434">
                IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL DAMAGES,
                LOSSES, AND CAUSES OF ACTION EXCEED THE AMOUNT PAID BY YOU, IF
                ANY, FOR ACCESSING THE SERVICES IN THE TWELVE (12) MONTHS PRIOR
                TO THE CLAIM, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS
                GREATER.
              </p>
            </section>

            <section className="mb-8" data-id="element-10435">
              <h2 className="text-xl font-bold mb-4" data-id="element-10436">9. Indemnification</h2>
              <p className="text-gray-700 mb-4" data-id="element-10437">
                You agree to indemnify, defend, and hold harmless ToxicDetector,
                its officers, directors, employees, agents, and affiliates from
                and against any claims, liabilities, damages, losses, costs,
                expenses, or fees (including reasonable attorneys' fees) arising
                from:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10438">
                <li data-id="element-10439">Your use or misuse of the Services</li>
                <li data-id="element-10440">Your violation of these Terms</li>
                <li data-id="element-10441">Your violation of any rights of another party</li>
                <li data-id="element-10442">Your User Content</li>
                <li data-id="element-10443">Your violation of any applicable laws or regulations</li>
              </ul>
            </section>

            <section className="mb-8" data-id="element-10444">
              <h2 className="text-xl font-bold mb-4" data-id="element-10445">10. Termination</h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10446">
                10.1 Termination by You
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10447">
                You may terminate your account at any time by accessing your
                Settings and selecting the account deletion option. Upon
                termination, your access to the Services will cease immediately.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10448">
                10.2 Termination by Us
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10449">
                We reserve the right to suspend or terminate your account and
                access to the Services at any time, with or without notice, for
                any reason, including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10450">
                <li data-id="element-10451">Violation of these Terms or our policies</li>
                <li data-id="element-10452">Fraudulent, abusive, or illegal activity</li>
                <li data-id="element-10453">Extended periods of inactivity</li>
                <li data-id="element-10454">Technical or security reasons</li>
                <li data-id="element-10455">At our sole discretion</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10456">
                10.3 Effect of Termination
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10457">Upon termination:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10458">
                <li data-id="element-10459">Your right to use the Services will immediately cease</li>
                <li data-id="element-10460">
                  We will delete your account data according to our data
                  retention policy
                </li>
                <li data-id="element-10461">
                  Provisions that by their nature should survive termination
                  will remain in effect
                </li>
                <li data-id="element-10462">
                  You remain liable for any obligations incurred prior to
                  termination
                </li>
              </ul>
            </section>

            <section className="mb-8" data-id="element-10463">
              <h2 className="text-xl font-bold mb-4" data-id="element-10464">
                11. Third-Party Services and Links
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-10465">
                Our Services may contain links to third-party websites,
                services, or resources. We do not endorse and are not
                responsible for:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10466">
                <li data-id="element-10467">
                  The availability, accuracy, or content of third-party services
                </li>
                <li data-id="element-10468">Third-party privacy practices or terms of service</li>
                <li data-id="element-10469">Any damages or losses caused by third-party services</li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-10470">
                Your interactions with third-party services are solely between
                you and the third party. We encourage you to review the terms
                and privacy policies of any third-party services you access.
              </p>
            </section>

            <section className="mb-8" data-id="element-10471">
              <h2 className="text-xl font-bold mb-4" data-id="element-10472">
                12. Modifications to Services and Terms
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10473">
                12.1 Service Changes
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10474">
                We reserve the right to modify, suspend, or discontinue any
                aspect of the Services at any time, with or without notice. We
                are not liable to you or any third party for any modification,
                suspension, or discontinuation of the Services.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10475">12.2 Terms Changes</h3>
              <p className="text-gray-700 mb-4" data-id="element-10476">
                We may revise these Terms at any time. Material changes will be
                notified through:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10477">
                <li data-id="element-10478">Email notification to your registered email address</li>
                <li data-id="element-10479">Prominent notice on our website or application</li>
                <li data-id="element-10480">Updated "Last Updated" date at the top of these Terms</li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-10481">
                Your continued use of the Services after changes become
                effective constitutes acceptance of the revised Terms. If you do
                not agree to the changes, you must stop using the Services and
                delete your account.
              </p>
            </section>

            <section className="mb-8" data-id="element-10482">
              <h2 className="text-xl font-bold mb-4" data-id="element-10483">
                13. Governing Law and Jurisdiction
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-10484">
                These Terms shall be governed by and construed in accordance
                with the laws of the United States, without regard to its
                conflict of law provisions.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-10485">
                You agree to submit to the personal and exclusive jurisdiction
                of the courts located within the United States for the
                resolution of any disputes arising from these Terms or your use
                of the Services.
              </p>
            </section>

            <section className="mb-8" data-id="element-10486">
              <h2 className="text-xl font-bold mb-4" data-id="element-10487">
                14. Dispute Resolution and Arbitration
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10488">
                14.1 Informal Resolution
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10489">
                Before filing a claim, you agree to contact us at
                toxicdetector25@gmail.com to attempt to resolve the dispute
                informally. We will attempt to resolve the dispute within 60
                days.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10490">
                14.2 Binding Arbitration
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10491">
                If we cannot resolve the dispute informally, any dispute arising
                from these Terms or the Services shall be resolved through
                binding arbitration in accordance with the American Arbitration
                Association's rules, except as modified by these Terms.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10492">
                14.3 Class Action Waiver
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10493">
                <strong data-id="element-10494">
                  YOU AND TOXICDETECTOR AGREE THAT EACH MAY BRING CLAIMS AGAINST
                  THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT AS A
                  PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR
                  REPRESENTATIVE PROCEEDING.
                </strong>
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10495">14.4 Exceptions</h3>
              <p className="text-gray-700 mb-4" data-id="element-10496">
                Either party may seek injunctive or other equitable relief in
                any court of competent jurisdiction to prevent actual or
                threatened infringement, misappropriation, or violation of
                intellectual property rights.
              </p>
            </section>

            <section className="mb-8" data-id="element-10497">
              <h2 className="text-xl font-bold mb-4" data-id="element-10498">15. General Provisions</h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10499">
                15.1 Entire Agreement
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10500">
                These Terms, together with our Privacy Policy, constitute the
                entire agreement between you and ToxicDetector regarding the
                Services and supersede all prior agreements and understandings.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10501">15.2 Severability</h3>
              <p className="text-gray-700 mb-4" data-id="element-10502">
                If any provision of these Terms is found to be invalid or
                unenforceable, the remaining provisions will remain in full
                force and effect.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10503">15.3 Waiver</h3>
              <p className="text-gray-700 mb-4" data-id="element-10504">
                Our failure to enforce any right or provision of these Terms
                will not be deemed a waiver of such right or provision.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10505">15.4 Assignment</h3>
              <p className="text-gray-700 mb-4" data-id="element-10506">
                You may not assign or transfer these Terms or your rights
                hereunder without our prior written consent. We may assign these
                Terms without restriction.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10507">15.5 Force Majeure</h3>
              <p className="text-gray-700 mb-4" data-id="element-10508">
                We shall not be liable for any failure or delay in performance
                due to circumstances beyond our reasonable control, including
                acts of God, war, terrorism, riots, natural disasters, or
                government actions.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10509">15.6 No Agency</h3>
              <p className="text-gray-700 mb-4" data-id="element-10510">
                No agency, partnership, joint venture, or employment
                relationship is created between you and ToxicDetector as a
                result of these Terms or your use of the Services.
              </p>
            </section>

            <section className="mb-8" data-id="element-10511">
              <h2 className="text-xl font-bold mb-4" data-id="element-10512">
                16. Contact Information
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-10513">
                If you have questions about these Terms, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-10514">
                <p className="text-gray-700 mb-2" data-id="element-10515">
                  <strong data-id="element-10516">Email:</strong> toxicdetector25@gmail.com
                </p>
                <p className="text-gray-700 mb-2" data-id="element-10517">
                  <strong data-id="element-10518">Support:</strong> support@toxicdetector.com
                </p>
                <p className="text-gray-700" data-id="element-10519">
                  <strong data-id="element-10520">General Inquiries:</strong> toxicdetector25@gmail.com
                </p>
              </div>
            </section>

            <section className="mb-8 bg-red-50 p-6 rounded-xl border border-red-100" data-id="element-10521">
              <h2 className="text-xl font-bold mb-4 text-red-900" data-id="element-10522">
                17. Liability & Usage Disclosure (Safe-Scalability)
              </h2>
              <p className="text-gray-800 mb-4" data-id="element-10523">
                ToxicDetector.ai is an AI-powered linguistic analysis tool
                designed for educational, entertainment, and self-validation
                purposes only.
              </p>
              <ul className="list-disc pl-6 text-gray-800 mb-4 space-y-2" data-id="element-10524">
                <li data-id="element-10525">
                  <strong data-id="element-10526">Not Clinical Advice:</strong> While our algorithms are
                  trained on behavioral patterns, ToxicDetector does not provide
                  medical, psychological, or psychiatric diagnoses. It is not a
                  substitute for professional therapy or mental health
                  counseling.
                </li>
                <li data-id="element-10527">
                  <strong data-id="element-10528">Not Legal Evidence:</strong> The "Forensic Dossier"
                  and "Toxicity Scores" are digital interpretations of text
                  patterns and are not certified for use as primary evidence in
                  legal proceedings or divorce litigation.
                </li>
                <li data-id="element-10529">
                  <strong data-id="element-10530">Data Privacy:</strong> We utilize AES-256 encryption.
                  By using this tool, you confirm you have the right to analyze
                  the uploaded text. ToxicDetector is not responsible for
                  interpersonal outcomes resulting from the use of our reports.
                </li>
                <li data-id="element-10531">
                  <strong data-id="element-10532">Accuracy:</strong> AI is a pattern-recognition tool.
                  Results are probabilistic, not absolute truths.
                </li>
              </ul>
            </section>

            <section className="mb-8 bg-gray-50 p-6 rounded-xl border border-gray-200" data-id="element-10533">
              <h2 className="text-xl font-bold mb-4" data-id="element-10534">
                18. Refund Policy: Digital Forensic Scans
              </h2>
              <p className="text-gray-800 font-semibold mb-2" data-id="element-10535">
                Final Sale Notice: Due to the immediate nature of AI-driven
                linguistic analysis, all completed scans are Final and
                Non-Refundable.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-10536">
                Once the Forensic Engine processes your data and generates your
                Toxicity Score, the digital value—derived from our proprietary
                algorithms and server processing—has been fully delivered. We do
                not offer refunds based on the 'result' of a scan (e.g., if you
                disagree with a 'Saint' or 'Toxic' rating), as the AI provides
                an objective analysis based on the text provided.
              </p>
              <p className="text-gray-700" data-id="element-10537">
                If you experience a technical error where a report is not
                generated, please contact Support@ToxicDetector.com for a manual
                re-run.
              </p>
            </section>

            <section className="mb-8" data-id="element-10538">
              <h2 className="text-xl font-bold mb-4" data-id="element-10539">19. Acknowledgment</h2>
              <p className="text-gray-700 mb-4" data-id="element-10540">
                BY USING TOXICDETECTOR, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE
                TERMS OF SERVICE, UNDERSTAND THEM, AND AGREE TO BE BOUND BY
                THEM. IF YOU DO NOT AGREE TO THESE TERMS, YOU MUST NOT USE OUR
                SERVICES.
              </p>
            </section>

            <div className="border-t border-gray-200 pt-6 mt-8" data-id="element-10541">
              <p className="text-gray-600 text-sm text-center" data-id="element-10542">
                © {new Date().getFullYear()} ToxicDetector. All rights
                reserved.
              </p>
              <p className="text-gray-500 text-xs text-center mt-2" data-id="element-10543">
                These Terms of Service were last updated on{' '}
                {new Date().toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>;
}